import { SystemBase } from "@inter-ikea-core/core-toolbox";
import { ComponentIds, HierarchyUtils, } from "@inter-ikea-core/core";
import { COMPONENT_IDS } from "@inter-ikea-storagetwo/range/dist/storage2ComponentIDs";
import { getRootMountingRail } from "../functions/MountingRails";
import { getUprights } from "../utils/sectionsHelpers";
import { MountingRailConnectionService } from "../utils/ConnectorService";
import { getMountingRailIndexInSolution, indexMountingRailsInSolution, } from "../utils/mountingRailHelpers";
import { NO_UPRIGHTS } from "../constants/errors";
import { UPRIGHT_TO_MOUNTING_RAIL_LEFT } from "../constants/connectorKeys";
import { ENTITY_NULL } from "@inter-ikea-storagetwo/range/dist/constants/errors";
import { getWallConnectorKey } from "../utils/connectorHelpers";
export default class RemoveSectionSystem extends SystemBase {
    constructor(core) {
        super(COMPONENT_IDS.REQUEST_DESTRUCTION_COMPONENT);
        this.core = core;
        this.doingChangesOnComponent = [];
        this.systemsBeforeMe = null;
        this.systemsAfterMe = null;
        this.id = "RemoveSectionSystem";
        /**
         * Handle removal of a section on the far right of a solution
         * @param mountingRail
         */
        this.handleRemoveLastSection = (mountingRail) => {
            const { leftUpright, rightUpright } = getUprights(mountingRail);
            if (!leftUpright || !rightUpright)
                throw new Error(NO_UPRIGHTS);
            HierarchyUtils.children(leftUpright).forEach((child) => child.hasComponent(COMPONENT_IDS.INSERT) && child.dispose());
            rightUpright.dispose();
            mountingRail.dispose();
        };
        /**
         * Handle removal of a section not on an edge in a solution
         * @param core
         * @param mountingRail
         */
        this.handleRemoveSectionInMiddle = (core, mountingRail) => {
            const { leftUpright, rightUpright } = getUprights(mountingRail);
            const wall = HierarchyUtils.parent(getRootMountingRail(mountingRail));
            if (!leftUpright || !rightUpright || !wall)
                throw new Error(ENTITY_NULL);
            const indexedMountingRails = indexMountingRailsInSolution(mountingRail);
            const indexOfMountingRail = getMountingRailIndexInSolution(mountingRail);
            const mountingRailToTheRight = indexedMountingRails[indexOfMountingRail + 1];
            const wallKey = getWallConnectorKey(mountingRail);
            const mountingRailConnectionService = MountingRailConnectionService(core);
            HierarchyUtils.children(leftUpright).forEach((child) => child.hasComponent(COMPONENT_IDS.INSERT) && child.dispose());
            mountingRailConnectionService.disconnect(mountingRail);
            mountingRailConnectionService.disconnect(rightUpright);
            mountingRailConnectionService.disconnect(mountingRailToTheRight);
            mountingRail.dispose();
            mountingRailConnectionService
                .connect(rightUpright)
                .to(mountingRailToTheRight)
                .atConnector(UPRIGHT_TO_MOUNTING_RAIL_LEFT);
            mountingRailConnectionService
                .connect(mountingRailToTheRight)
                .to(wall)
                .atConnector(wallKey);
        };
        /**
         * Handle removal of a section on the far left of a solution
         * @param core
         * @param mountingRail
         */
        this.handleRemoveFirstSection = (core, mountingRail) => {
            const { rightUpright } = getUprights(mountingRail);
            const wall = HierarchyUtils.parent(getRootMountingRail(mountingRail));
            if (!rightUpright || !wall)
                throw new Error(ENTITY_NULL);
            const indexedMountingRails = indexMountingRailsInSolution(mountingRail);
            const indexOfMountingRail = getMountingRailIndexInSolution(mountingRail);
            const mountingRailToTheRight = indexedMountingRails[indexOfMountingRail + 1];
            const wallKey = getWallConnectorKey(mountingRail);
            const mountingRailConnectionService = MountingRailConnectionService(core);
            mountingRailConnectionService.disconnect(rightUpright);
            mountingRailConnectionService.disconnect(mountingRailToTheRight);
            mountingRail.dispose();
            mountingRailConnectionService
                .connect(rightUpright)
                .to(mountingRailToTheRight)
                .atConnector(UPRIGHT_TO_MOUNTING_RAIL_LEFT);
            mountingRailConnectionService
                .connect(mountingRailToTheRight)
                .to(wall)
                .atConnector(wallKey);
        };
    }
    onAddComponent(_entity, _newComponent) {
        var _a;
        super.onAddComponent(_entity, _newComponent);
        const { leftUpright, rightUpright } = getUprights(_entity);
        // Remove the selected & outlined components before
        // taking snapshot so they're not re-added on undo.
        _entity.removeComponent(ComponentIds.SELECTED_COMPONENT_ID);
        leftUpright === null || leftUpright === void 0 ? void 0 : leftUpright.removeComponent(ComponentIds.OUTLINED_COMPONENT_ID);
        rightUpright === null || rightUpright === void 0 ? void 0 : rightUpright.removeComponent(ComponentIds.OUTLINED_COMPONENT_ID);
        (_a = this.core.modules.history()) === null || _a === void 0 ? void 0 : _a.takeSnapshot("remove section");
        const indexedMountingRails = indexMountingRailsInSolution(_entity);
        const indexInSolution = getMountingRailIndexInSolution(_entity);
        const isLastSectionInSolution = Object.keys(indexedMountingRails).length - 1 === indexInSolution;
        const isFirstSection = indexInSolution === 0;
        if (isLastSectionInSolution)
            return this.handleRemoveLastSection(_entity);
        if (isFirstSection)
            return this.handleRemoveFirstSection(this.core, _entity);
        return this.handleRemoveSectionInMiddle(this.core, _entity);
    }
}
